import { useIntl } from 'react-intl'
import React, { useState, useRef, useEffect, useMemo } from 'react'
import { CountryRegionData } from 'react-country-region-selector'
import { safeFill, resolveColor } from '../styles/colorContrast'

interface Question {
  id: string
  text: string
  type: 'BOOLEAN' | 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'RATING' | 'COUNTRY'
  required: boolean
  order: number
  options: string[]
}

interface QuestionRendererProps {
  question: Question
  value: any
  onChange: (value: any) => void
  disabled?: boolean
  ratingLabels?: Record<string, string>
  groupColor?: string
}

const DROPDOWN_THRESHOLD = 6

// ─── Rating (0-5) ────────────────────────────────────────────────────

const RatingRenderer: React.FC<QuestionRendererProps> = ({ question: _question, value, onChange, disabled, ratingLabels, groupColor }) => {
  const intl = useIntl()
  const ratings = [0, 1, 2, 3, 4, 5]
  const defaultLabels = ['N/A', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']
  const hasCustomLabels = ratingLabels && Object.keys(ratingLabels).length > 0
  // Which option's description to show: the hovered/focused one, falling back
  // to the selected one (so touch devices still see it after they pick).
  const [activeRating, setActiveRating] = useState<number | null>(null)
  const shownRating = activeRating != null ? activeRating : (value != null ? value : null)
  const shownLabel = hasCustomLabels && shownRating != null ? ratingLabels![String(shownRating)] : null
  const descId = `rating-desc-${_question.id}`
  // Per-survey group colors are content (names like "yellow", or author-picked hex),
  // so resolve to an AA-safe {background, text} pair instead of hardcoding white text.
  const fill = groupColor ? safeFill(groupColor) : null
  const groupHex = groupColor ? resolveColor(groupColor) : null

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
        {ratings.map((rating) => {
          const isSelected = value === rating
          const customLabel = hasCustomLabels ? ratingLabels![String(rating)] : undefined
          return (
            <button
              key={rating}
              type="button"
              disabled={disabled}
              onClick={() => onChange(rating)}
              onMouseEnter={() => hasCustomLabels && setActiveRating(rating)}
              onMouseLeave={() => setActiveRating(null)}
              onFocus={() => hasCustomLabels && setActiveRating(rating)}
              onBlur={() => setActiveRating(null)}
              aria-describedby={hasCustomLabels ? descId : undefined}
              aria-label={customLabel ? `${rating}: ${customLabel}` : `${rating} ${defaultLabels[rating]}`}
              className={`
                flex flex-col items-center justify-center py-4 px-2 rounded-lg border-2 transition-all duration-150
                ${isSelected && !fill
                  ? 'bg-primary border-primary text-white shadow-md scale-105'
                  : !isSelected
                    ? 'bg-surface border-border-light text-foreground hover:border-primary hover:bg-primary/10'
                    : 'shadow-md scale-105'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              style={isSelected && fill ? { backgroundColor: fill.background, borderColor: fill.background, color: fill.text } : undefined}
            >
              <span className="text-2xl font-bold">{rating}</span>
              {!hasCustomLabels && (
                <span className={`text-xs mt-1 ${isSelected ? 'opacity-70' : 'text-foreground-muted'}`}>
                  {defaultLabels[rating]}
                </span>
              )}
            </button>
          )
        })}
      </div>
      {hasCustomLabels && (
        <div
          id={descId}
          role="tooltip"
          aria-live="polite"
          className="mt-4 p-3 rounded-lg border min-h-[3.25rem] flex items-center"
          style={groupHex
            ? { backgroundColor: `${groupHex}15`, borderColor: `${groupHex}40` }
            : { backgroundColor: 'var(--color-primary-light, rgba(99,102,241,0.1))', borderColor: 'var(--color-primary-light, rgba(99,102,241,0.3))' }
          }
        >
          {shownLabel ? (
            <p className="text-sm text-foreground">
              <span className="font-semibold" style={fill ? { color: fill.background } : { color: 'var(--color-primary)' }}>{shownRating}/5:</span> {shownLabel}
            </p>
          ) : (
            <p className="text-sm text-foreground-muted">{intl.formatMessage({ id: 'take.question.hoverHint' })}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Boolean (Yes/No) ────────────────────────────────────────────────

const BooleanRenderer: React.FC<QuestionRendererProps> = ({ question: _question, value, onChange, disabled }) => {
  const intl = useIntl()
  return (
    <div className="grid grid-cols-2 gap-4">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(true)}
        className={`
          flex items-center justify-center py-6 px-6 rounded-lg border-2 text-lg font-semibold transition-all duration-150
          ${value === true
            ? 'bg-primary border-primary text-white shadow-md'
            : 'bg-surface border-border-light text-foreground hover:border-primary hover:bg-primary/10'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        {intl.formatMessage({ id: 'take.question.yes' })}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(false)}
        className={`
          flex items-center justify-center py-6 px-6 rounded-lg border-2 text-lg font-semibold transition-all duration-150
          ${value === false
            ? 'bg-primary border-primary text-white shadow-md'
            : 'bg-surface border-border-light text-foreground hover:border-primary hover:bg-primary/10'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        {intl.formatMessage({ id: 'take.question.no' })}
      </button>
    </div>
  )
}

// ─── Searchable Dropdown (shared by Single Choice >6 and Country fallback) ──

const SearchableDropdown: React.FC<{
  options: string[]
  value: string | null
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
}> = ({ options, value, onChange, disabled, placeholder = 'Search...' }) => {
  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()))

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-between
          ${value ? 'border-primary bg-primary/5 text-foreground' : 'border-border-light bg-surface text-foreground-muted'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary-light'}
        `}
      >
        <span className={value ? 'font-medium' : ''}>{value || placeholder}</span>
        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-surface border border-border rounded-lg shadow-lg max-h-96 overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-border-light rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-72">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-sm text-foreground-muted">{intl.formatMessage({ id: 'take.question.noMatches' })}</p>
            ) : (
              filtered.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => { onChange(option); setIsOpen(false); setSearch('') }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-primary/10 transition-colors
                    ${value === option ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'}
                  `}
                >
                  {option}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Single Choice: radio buttons (<=6) or searchable dropdown (>6) ──

const SingleChoiceRenderer: React.FC<QuestionRendererProps> = ({ question, value, onChange, disabled }) => {
  const intl = useIntl()
  if (question.options.length > DROPDOWN_THRESHOLD) {
    return (
      <SearchableDropdown
        options={question.options}
        value={value || null}
        onChange={onChange}
        disabled={disabled}
        placeholder={intl.formatMessage({ id: 'take.question.searchSelectPlaceholder' })}
      />
    )
  }

  return (
    <div className="space-y-3">
      {question.options?.map((option) => (
        <button
          key={option}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option)}
          className={`
            w-full text-left px-5 py-4 rounded-lg border-2 transition-all duration-150 flex items-center
            ${value === option
              ? 'bg-primary/10 border-primary text-foreground'
              : 'bg-surface border-border-light text-foreground hover:border-primary-light hover:bg-background'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <div className={`
            w-5 h-5 rounded-full border-2 mr-4 flex-shrink-0 flex items-center justify-center
            ${value === option ? 'border-primary bg-primary' : 'border-border'}
          `}>
            {value === option && (
              <div className="w-2 h-2 rounded-full bg-surface" />
            )}
          </div>
          <span className="text-base font-medium">{option}</span>
        </button>
      ))}
    </div>
  )
}

// ─── Multiple Choice: checkboxes with search for many options ────────

const MultipleChoiceRenderer: React.FC<QuestionRendererProps> = ({ question, value, onChange, disabled }) => {
  const intl = useIntl()
  const selected: string[] = Array.isArray(value) ? value : (value ? [value] : [])
  const [search, setSearch] = useState('')
  const showSearch = question.options.length > DROPDOWN_THRESHOLD

  const toggle = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option))
    } else {
      onChange([...selected, option])
    }
  }

  const filtered = showSearch && search
    ? question.options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : question.options

  return (
    <div>
      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selected.map(s => (
            <span key={s} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
              {s}
              <button type="button" onClick={() => toggle(s)} className="hover:text-primary-dark">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search */}
      {showSearch && (
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={intl.formatMessage({ id: 'take.question.searchOptionsPlaceholder' })}
          className="w-full px-4 py-2.5 mb-3 border-2 border-border-light rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
      )}

      {/* Options */}
      <div className={`space-y-2 ${showSearch ? 'max-h-64 overflow-y-auto' : ''}`}>
        {filtered.map((option) => {
          const isChecked = selected.includes(option)
          return (
            <button
              key={option}
              type="button"
              disabled={disabled}
              onClick={() => toggle(option)}
              className={`
                w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-150 flex items-center
                ${isChecked
                  ? 'bg-primary/10 border-primary text-foreground'
                  : 'bg-surface border-border-light text-foreground hover:border-primary-light hover:bg-background'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className={`
                w-5 h-5 rounded-sm border-2 mr-3 flex-shrink-0 flex items-center justify-center
                ${isChecked ? 'border-primary bg-primary' : 'border-border'}
              `}>
                {isChecked && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-sm font-medium">{option}</span>
            </button>
          )
        })}
        {filtered.length === 0 && search && (
          <p className="text-sm text-foreground-muted py-2 px-1">No options match "{search}"</p>
        )}
      </div>
    </div>
  )
}

// ─── Country Selector (searchable dropdowns using library data) ──────

const countryRegionRaw = (CountryRegionData as any).default || CountryRegionData
const COUNTRY_NAMES: string[] = (countryRegionRaw as string[][]).map((c: string[]) => c[0])

function getRegionsForCountry(country: string): string[] {
  const entry = (countryRegionRaw as string[][]).find((c: string[]) => c[0] === country)
  if (!entry || !entry[2]) return []
  return entry[2].split('|').map((r: string) => r.split('~')[0])
}

const CountryRenderer: React.FC<QuestionRendererProps> = ({ question: _question, value, onChange, disabled }) => {
  const intl = useIntl()
  const countryValue = typeof value === 'object' && value !== null ? value.country || '' : (value || '')
  const regionValue = typeof value === 'object' && value !== null ? value.region || '' : ''

  const regions = useMemo(() => getRegionsForCountry(countryValue), [countryValue])

  const handleCountryChange = (country: string) => {
    onChange({ country, region: '' })
  }

  const handleRegionChange = (region: string) => {
    onChange({ country: countryValue, region })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground-muted mb-1">{intl.formatMessage({ id: 'take.question.countryLabel' })}</label>
        <SearchableDropdown
          options={COUNTRY_NAMES}
          value={countryValue || null}
          onChange={handleCountryChange}
          disabled={disabled}
          placeholder={intl.formatMessage({ id: 'take.question.countrySearchPlaceholder' })}
        />
      </div>
      {countryValue && regions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-foreground-muted mb-1">Region / Province</label>
          <SearchableDropdown
            options={regions}
            value={regionValue || null}
            onChange={handleRegionChange}
            disabled={disabled}
            placeholder={intl.formatMessage({ id: 'take.question.regionSearchPlaceholder' })}
          />
        </div>
      )}
    </div>
  )
}

// ─── Text ────────────────────────────────────────────────────────────

const TextRenderer: React.FC<QuestionRendererProps> = ({ question: _question, value, onChange, disabled }) => {
  const intl = useIntl()
  return (
    <div>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={4}
        className="block w-full border-2 border-border-light rounded-lg shadow-sm px-4 py-3 text-base
                   focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                   disabled:opacity-50 disabled:cursor-not-allowed
                   placeholder-foreground-muted"
        placeholder={intl.formatMessage({ id: 'take.question.textPlaceholder' })}
      />
    </div>
  )
}

// ─── Main Renderer ───────────────────────────────────────────────────

const QuestionRenderer: React.FC<QuestionRendererProps> = (props) => {
  switch (props.question.type) {
    case 'RATING':
      return <RatingRenderer {...props} />
    case 'BOOLEAN':
      return <BooleanRenderer {...props} />
    case 'SINGLE_CHOICE':
      return <SingleChoiceRenderer {...props} />
    case 'MULTIPLE_CHOICE':
      return <MultipleChoiceRenderer {...props} />
    case 'COUNTRY':
      return <CountryRenderer {...props} />
    case 'TEXT':
      return <TextRenderer {...props} />
    default:
      return <p className="text-foreground-muted">Unsupported question type: {props.question.type}</p>
  }
}

export default QuestionRenderer
